import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { attrGroupLinkedTables } from "components/PimDashboard/data";
import { attrValAssocObj } from "components/PimDashboard/DashboardComponents/Designer/DesignerAttributes/data";
import RenderStyleInputField from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderStyleInputFieldNU/RenderStyleInputField";
import { removeDropdownOptionsBasedonDependency } from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderAttributes/RenderAttributeInputs";
import { isColorRejected } from "components/PimDashboard/DashboardComponents/Designer/pendingRequiredFields";
import { categoryDependentChoices, reconcileCategorySelects, toMultiValue } from "../categorySelectReset";

const cloneStyle = (style) => JSON.parse(JSON.stringify(style || {}));

const colorLabel = (row) => row?.color || row?.colour || row?.option_id || `Color ${row?.id}`;

const FieldInput = ({ attr, data, onChange, allAttributes }) => {
	const options = removeDropdownOptionsBasedonDependency(attr, data, attr?.[attrValAssocObj], allAttributes);
	return (
		<RenderStyleInputField
			column={{ ...attr, [attrValAssocObj]: options }}
			data={data}
			setData={() => {}}
			selectOptObjName={attrValAssocObj}
			width="32%"
			longWidth="48%"
			attrGrp={{ linked_table: attr.linked_table }}
			error={false}
			errorObj={{}}
			setError={() => {}}
			onChange={(event) => onChange(attr, event.target.value)}
		/>
	);
};

const ChangeCategoryModal = ({ open, onClose, selectedStyle, allAttributes = [], onSubmit, loading = false }) => {
	const styleKey = attrGroupLinkedTables.style.key;
	const detailsKey = attrGroupLinkedTables.style_details.key;
	const colorKey = attrGroupLinkedTables.color.key;
	const [draft, setDraft] = useState(() => cloneStyle(selectedStyle));

	useEffect(() => {
		if (!open) return;
		const next = cloneStyle(selectedStyle);
		(allAttributes || []).forEach((attr) => {
			if (attr.input_type !== "MULTIPLE_SELECT") return;
			if (attr.linked_table === colorKey) {
				next[colorKey] = (next[colorKey] || []).map((row) => ({ ...row, [attr.key]: toMultiValue(row[attr.key]) }));
				return;
			}
			if (next[attr.linked_table]) next[attr.linked_table][attr.key] = toMultiValue(next[attr.linked_table][attr.key]);
		});
		setDraft(next);
	}, [open, selectedStyle, allAttributes]);

	const categoryAttr = useMemo(
		() => (allAttributes || []).find((attr) => attr.key === "category"),
		[allAttributes],
	);
	const directChoices = useMemo(() => categoryDependentChoices(allAttributes), [allAttributes]);
	const visibleChoices = useMemo(() => {
		const directKeys = new Set(directChoices.map((attr) => attr.key));
		const changed = (allAttributes || []).filter((attr) => {
			if (directKeys.has(attr.key) || attr.key === "category") return false;
			if (attr.input_type !== "SELECT" && attr.input_type !== "MULTIPLE_SELECT") return false;
			if (attr.linked_table === colorKey) {
				return (draft?.[colorKey] || []).some((row) => {
					if (isColorRejected(row)) return false;
					const previous = (selectedStyle?.[colorKey] || []).find((item) => String(item.id) === String(row.id));
					const multiple = attr.input_type === "MULTIPLE_SELECT";
					if (multiple) {
						const before = toMultiValue(previous?.[attr.key]);
						const after = toMultiValue(row?.[attr.key]);
						return before.join("|") !== after.join("|");
					}
					return String(previous?.[attr.key] ?? "") !== String(row?.[attr.key] ?? "");
				});
			}
			const before = selectedStyle?.[attr.linked_table]?.[attr.key];
			const after = draft?.[attr.linked_table]?.[attr.key];
			if (attr.input_type === "MULTIPLE_SELECT") return toMultiValue(before).join("|") !== toMultiValue(after).join("|");
			return String(before ?? "") !== String(after ?? "");
		});
		return [...directChoices, ...changed];
	}, [allAttributes, directChoices, draft, selectedStyle]);

	const applyValue = (attr, value, colorId) => {
		setDraft((prev) => {
			const next = cloneStyle(prev);
			if (attr.linked_table === colorKey) {
				next[colorKey] = (next[colorKey] || []).map((row) =>
					String(row.id) === String(colorId) ? { ...row, [attr.key]: value } : row,
				);
			} else if (attr.key === "category") {
				next[styleKey] = { ...(next[styleKey] || {}), category: value || "" };
			} else {
				next[attr.linked_table] = { ...(next[attr.linked_table] || {}), [attr.key]: value };
			}
			return reconcileCategorySelects(next, allAttributes);
		});
	};

	const activeColors = (draft?.[colorKey] || []).filter((row) => !isColorRejected(row));
	const styleChoices = visibleChoices.filter((attr) => attr.linked_table === styleKey);
	const detailChoices = visibleChoices.filter((attr) => attr.linked_table === detailsKey);
	const colorChoices = visibleChoices.filter((attr) => attr.linked_table === colorKey);

	const handleSubmit = (event) => {
		event.preventDefault();
		const choiceKeys = (table) =>
			visibleChoices.filter((attr) => attr.linked_table === table).map((attr) => attr.key);
		const pick = (row, keys) =>
			keys.reduce((acc, key) => {
				if (row && Object.prototype.hasOwnProperty.call(row, key)) acc[key] = row[key];
				return acc;
			}, {});

		onSubmit(
			{
				style_id: draft?.[styleKey]?.style_id,
				category: draft?.[styleKey]?.category,
				style: pick(draft?.[styleKey], choiceKeys(styleKey)),
				style_details: pick(draft?.[detailsKey], choiceKeys(detailsKey)),
				colors: activeColors.map((row) => ({
					id: row.id,
					...pick(row, choiceKeys(colorKey)),
				})),
			},
			onClose,
		);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md" component="form" onSubmit={handleSubmit}>
			<DialogTitle>Change Category</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Select and multiple-select values stay when they are still in the list for the new category. A value that is
					not in that list is cleared.
				</Typography>
				{categoryAttr && (
					<Box mb={2}>
						<FieldInput
							attr={{ ...categoryAttr, linked_table: styleKey }}
							data={draft}
							allAttributes={allAttributes}
							onChange={applyValue}
						/>
					</Box>
				)}
				{!!styleChoices.length && (
					<Box mb={2}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Style
						</Typography>
						<Box display="flex" flexWrap="wrap" columnGap={2} rowGap={1}>
							{styleChoices.map((attr) => (
								<FieldInput
									key={attr.key}
									attr={attr}
									data={draft}
									allAttributes={allAttributes}
									onChange={applyValue}
								/>
							))}
						</Box>
					</Box>
				)}
				{!!detailChoices.length && (
					<Box mb={2}>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Style details
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1, width: "100%" }}>
							Change the category dependent fields if needed.
						</Typography>
						<Box display="flex" flexWrap="wrap" columnGap={2} rowGap={1}>
							{detailChoices.map((attr) => (
								<FieldInput
									key={attr.key}
									attr={attr}
									data={draft}
									allAttributes={allAttributes}
									onChange={applyValue}
								/>
							))}
						</Box>
					</Box>
				)}
				{colorChoices.length > 0 &&
					activeColors.map((row) => (
						<Box key={row.id} mb={2}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								{colorLabel(row)}
							</Typography>
							<Box display="flex" flexWrap="wrap" columnGap={2} rowGap={1}>
								{colorChoices.map((attr) => (
									<FieldInput
										key={`${row.id}-${attr.key}`}
										attr={attr}
										data={{ ...draft, [colorKey]: row }}
										allAttributes={allAttributes}
										onChange={(field, value) => applyValue(field, value, row.id)}
									/>
								))}
							</Box>
						</Box>
					))}
				{!visibleChoices.length && (
					<Typography variant="body2">No select fields depend on category. Only the category will change.</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={onClose} disabled={loading}>
					Cancel
				</Button>
				<Button type="submit" variant="contained" disabled={loading || !draft?.[styleKey]?.category}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ChangeCategoryModal;
